import { useEffect, useState } from 'react'

import { Card } from 'components'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useModal, usePersistForm } from 'hooks'
import useLoading from 'hooks/useLoading'
import useToggle from 'hooks/useToggle'
import { RegisterPendingServices } from 'services/NGT/RegisterPendingItems'
import { SPACING } from 'utils'
import { removeAccents } from 'utils/string.uitls'
import { TipoPendencia } from 'utils/types'

import {
  FilterPendingItems,
  ModalEditPendingItems,
  TablePendingItems
} from './components'

import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

const SCHEMA = yup.object({
  idTipoPendencia: yup.number().nullable().notRequired(),
  txTipo: yup.string().nullable().notRequired(),
  txDescricao: yup.string().nullable().notRequired()
})

export const RegisterPendingItems = () => {
  const { showModal } = useModal()
  const { loading, msgErro, setLoading, setMsgErro } = useLoading()
  const [listPendingItems, setListPendingItems] = useState<
    TipoPendencia[] | undefined
  >()
  const [openEditModal, toggleEditModal] = useToggle()
  const [selectedPending, setSelectedPending] = useState<
    TipoPendencia | undefined
  >()
  const [isDeleteModalOpen, toggleModal] = useToggle()
  const [idTipoPendenciaDel, setIdTipoPendenciaDel] = useState<
    number | undefined
  >()

  const { control, watch, setValue, reset, handleSubmit } = useForm<TipoPendencia>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      idTipoPendencia: null,
      txDescricao: '',
      txTipo: 'Todos'
    }
  })

  usePersistForm('ngt_recors_registration_search', {
    setValue,
    watch
  })

  const handleSearch = (form: TipoPendencia = {} as TipoPendencia) => {

    const requestData: TipoPendencia = {
      idTipoPendencia: form && form?.idTipoPendencia ? form.idTipoPendencia : null,
      txDescricao: form && form?.txDescricao ? form.txDescricao : null,
      txTipo: form && form?.txTipo && form?.txTipo == 'Todos' ? null : form.txTipo,
      isTelaCRE: false
    }

    setLoading('loading')
    RegisterPendingServices()
      .getAll(requestData)
      .then(({ data }) => {
        setListPendingItems(data)
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(err.title)
        // setLoading('error')
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: err.title,
          type: 'alert'
        })
      })
  }

  useEffect(() => {
    handleSearch(watch())
  }, [])

  const handleClearButton = () => {
    reset()
    handleSearch(watch())
  }

  const handleSelectPending = (currentPending: TipoPendencia) => {
    toggleEditModal()
    setSelectedPending(currentPending)
  }

  const handleOpenDeleteModal = (idTipoPendencia: number) => {
    toggleModal()
    setIdTipoPendenciaDel(idTipoPendencia)
  }

  const handleEditPending = (requestData: TipoPendencia) => {
    setLoading('loading')
    RegisterPendingServices()
      .patch({
        ...requestData
      })
      .then(({ data }) => {
        if (data?.sucesso) {
          const cpyPending = listPendingItems.slice()
          const indexCpy = cpyPending.findIndex(
            (pending) =>
              pending.idTipoPendencia ===
              (data.valor as TipoPendencia).idTipoPendencia
          )
          cpyPending[indexCpy] = data.valor as TipoPendencia
          setListPendingItems(cpyPending)
        }
        setLoading('idle')
        toggleEditModal()
        showModal({
          hideButtonCancel: true,
          message: 'Atualizado com sucesso',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
      })
      .catch((err) => {
        // setMsgErro(err.title)
        // setLoading('error')
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: err.title,
          type: 'alert'
        })
      })
  }

  const handleDeletePendingItems = () => {
    toggleModal()
    setLoading('loading')
    RegisterPendingServices()
      .del(idTipoPendenciaDel)
      .then(({ data }) => {
        if (data?.sucesso) {
          const cpyPending = listPendingItems.slice()
          setListPendingItems(
            cpyPending.filter((pd) => pd.idTipoPendencia !== idTipoPendenciaDel)
          )
          showModal({
            hideButtonCancel: true,
            message: 'Deletado com sucesso',
            txtButtonConfirm: 'Fechar',
            type: 'success'
          })
        }
        reset()
        handleSearch(watch())
        setLoading('idle')
      })
      .catch((err) => {
        // setMsgErro(err.title)
        // setLoading('error')
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: err.title,
          type: 'alert'
        })
      })
  }

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterPendingItems
          control={control}
          handleSubmit={handleSubmit}
          handleClearButton={handleClearButton}
          handleSearch={handleSearch}
        />
        <TablePendingItems
          tableData={listPendingItems}
          handleOpenDeleteModal={handleOpenDeleteModal}
          selectedPending={handleSelectPending}
        />
      </Card>
      <SuccessModal
        open={loading === 'error'}
        message={msgErro}
        type="error"
        hideButtonCancel
        handleConfirm={() => setLoading('idle')}
        txtButtonConfirm="Voltar"
      />
      <ModalEditPendingItems
        showModal={openEditModal}
        toggleModal={toggleEditModal}
        pendingData={selectedPending}
        onSubmit={handleEditPending}
      />
      <SuccessModal
        open={isDeleteModalOpen}
        message="Confirma exclusão (S/N)?"
        type="alert"
        txtButtonCancel="NÃO"
        txtButtonConfirm="SIM"
        handleCancel={toggleModal}
        handleConfirm={handleDeletePendingItems}
      />
    </>
  )
}
